import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { isUndefined } from "lodash";

import { RoundingMethod } from "../../../../../../../../../redux/constants/OrdersPage/order";
import OrderStatus from "../../../../../../../../../types/OrderStatus";
import useObjectEditor from "../../../../../../../../../hooks/useObjectEditor";
import { EditIcon } from "../../../../../../../../../icons/edit";
import {
	NumberInput,
	NumberInputMemo,
} from "../../../../../../../../../components/Orders";
import {
	Colors,
	StyledRow,
} from "../../../../../../../../../components/common";
import { useCurrencyGlobalSettings } from "../../../../../../../../../hooks";
import { useOrderModalContext } from "../../../../../../context";
import { OrderFormProps } from "../../../types/FormProps";
import { Green, Primary, Red } from "../../styled";

import Clickable from "./Clickable";

interface Props extends OrderFormProps {
	edit: boolean;
	setEdit: (edit: boolean) => void;
}

const Price: React.FC<Props> = ({ disabled, edit, setEdit, form, tab }) => {
	const { t } = useTranslation();
	const { loadingChangeCost, points, orderStatus, isArchiveEditSession } =
		useOrderModalContext();
	const currencyGlobalSettings = useCurrencyGlobalSettings();

	// Regular (non-archive) order editing keeps `price` in sync with
	// `additionalCost` via a live server-side cost recalculation
	// (see `OrderModal/index.tsx` -> `debounceCalculateCost`), which folds
	// any additional-cost delta into `price` after every change. Editing an
	// order from the archive reuses this same form but intentionally skips
	// that recalculation pipeline (per the "reuse the revert chain, don't
	// build a parallel calc pipeline" requirement), so without this
	// fallback the price label would never reflect a value picked here.
	//
	// This used to check `orderStatus === OrderStatus.CLOSED`, but `Edit`
	// reverts the order in place (status -> EXECUTING) *before* the price
	// field ever becomes editable (see `useOrderUpdate.tsx` /
	// `InfoModalOrder`'s `isEditingClosedOrder`), so that check was never
	// true while this input was actually reachable - the manually typed
	// price silently never reached `price`. `isArchiveEditSession` is the
	// same "mid archive-edit" signal `InfoModalOrder` uses for the
	// points-recalculation effect.
	// A taximeter order skips the live recalculation too (dev check F14).
	const syncPriceWithAdditionalCost = useMemo(
		() =>
			orderStatus === OrderStatus.CLOSED ||
			isArchiveEditSession ||
			!!tab.form?.processedByTaximeter,
		[orderStatus, isArchiveEditSession, tab.form?.processedByTaximeter],
	);
	const [value, onChangeValue] = useState<NumberInput.Value>({
		action: "set",
		value: 0,
	});

	const valueEditor = useObjectEditor(value, onChangeValue);

	const setAction = valueEditor.useSetter("action");
	const valueCost = valueEditor.useGetter("value");
	const setValue = valueEditor.useSetter("value");

	// Live form values. On the regular orders page `tab.form` is re-synced
	// after every change (setTabs), so it always matches the live form. In
	// the archive edit flow `tab.form` is a one-time static snapshot of the
	// loaded order, so reading it directly would never reflect
	// `form.setValue(...)` edits — subscribe to the live form state instead.
	const livePrice = useWatch({ control: form.control, name: "price" });
	const liveAdditionalCost = useWatch({
		control: form.control,
		name: "additionalCost",
	});

	const watchedPrice = useMemo(
		() => livePrice ?? tab.form?.price ?? 0,
		[livePrice, tab.form?.price],
	);

	const roundingMultipleValue = useMemo(
		() => tab.form?.priceSettings?.rounding?.multiple?.value ?? 0,
		[tab.form?.priceSettings?.rounding?.multiple?.value],
	);
	const roundingMultipleActive = useMemo(
		() => tab.form?.priceSettings?.rounding?.multiple?.active ?? false,
		[tab.form?.priceSettings?.rounding?.multiple?.active],
	);
	const roundingMethod = useMemo(
		() =>
			tab.form?.priceSettings?.rounding?.method ?? RoundingMethod.GENERAL,
		[tab.form?.priceSettings?.rounding?.method],
	);

	const precision = useMemo(
		() => tab.form?.priceSettings?.rounding?.precision ?? 2,
		[tab.form?.priceSettings?.rounding?.precision],
	);

	const additionalCost = useMemo(
		() => liveAdditionalCost ?? tab.form?.additionalCost ?? 0,
		[liveAdditionalCost, tab.form?.additionalCost],
	);
	const discountValue = useMemo(() => {
		const bonusAmount = tab.form?.discountCost?.bonusAmount || 0;
		const fixAmount = tab.form?.discountCost?.fixedAmount || 0;
		return bonusAmount + fixAmount;
	}, [
		tab.form?.discountCost?.bonusAmount,
		tab.form?.discountCost?.fixedAmount,
	]);

	const stringifyPrice = useCallback(
		(price: number | undefined) => {
			if (isUndefined(price)) return "-";

			let processedPrice = price;
			const multiplier = 10 ** precision;
			if (roundingMultipleActive) {
				processedPrice /= roundingMultipleValue;
			} else {
				processedPrice *= multiplier;
			}

			switch (roundingMethod) {
				case RoundingMethod.GENERAL:
					processedPrice =
						Math.round(processedPrice * multiplier) / multiplier;
					break;
				case RoundingMethod.CEIL:
					processedPrice =
						Math.ceil(processedPrice * multiplier) / multiplier;
					break;
				case RoundingMethod.FLOOR:
					processedPrice =
						Math.floor(processedPrice * multiplier) / multiplier;
					break;
				default:
					break;
			}

			if (roundingMultipleActive) {
				processedPrice *= roundingMultipleValue;
			} else {
				processedPrice /= 10 ** precision;
			}

			return `${processedPrice.toFixed(precision)}`.trim();
		},
		[
			precision,
			roundingMethod,
			roundingMultipleActive,
			roundingMultipleValue,
		],
	);

	useLayoutEffect(() => {
		if (valueCost !== watchedPrice) {
			setValue(Number(stringifyPrice(watchedPrice)));
		}
	}, [watchedPrice, setValue, valueCost, stringifyPrice]);

	const onChange = useCallback(
		(data: NumberInput.Value) => {
			setAction(data.action);

			// The base fare, i.e. `price` with the currently applied
			// `additionalCost` stripped back out. Used to fold a newly
			// picked additional-cost value back into `price` when there is
			// no live recalculation keeping them in sync (see
			// `syncPriceWithAdditionalCost` above).
			const basePrice = Number(watchedPrice) - Number(additionalCost);
			const applyAdditionalCost = (newAdditionalCost: number) => {
				form.setValue("additionalCost", newAdditionalCost);
				if (syncPriceWithAdditionalCost) {
					// A taximeter price is folded by the server to the
					// kopeck and never below zero (dev check F14).
					const nextPrice = tab.form?.processedByTaximeter
						? Math.max(
								0,
								Math.round(
									(basePrice + newAdditionalCost) * 100,
								) / 100,
						  )
						: Number(stringifyPrice(basePrice + newAdditionalCost));
					form.setValue("price", nextPrice);
				}
			};

			if (data.action === "set") {
				const dataValue = data.value;

				setValue(Number(stringifyPrice(dataValue)));
				if (dataValue >= 0) {
					applyAdditionalCost(
						dataValue -
							Number(watchedPrice) +
							Number(additionalCost),
					);
					setEdit(false);
					return;
				}

				if (dataValue < 0) {
					const prevPrice = basePrice;
					const cost = -(prevPrice - dataValue);

					if (cost + prevPrice <= 0) {
						applyAdditionalCost(
							-Number(stringifyPrice(prevPrice)),
						);
						setEdit(false);
						return;
					}

					applyAdditionalCost(Number(stringifyPrice(cost)));
				}
			} else {
				const dataValue = data.value;
				const payload = Number(additionalCost) + dataValue;
				const prevPrice = basePrice;
				const cost = dataValue + Number(additionalCost);

				if (cost + prevPrice <= 0) {
					applyAdditionalCost(-Number(stringifyPrice(prevPrice)));
					setEdit(false);
					return;
				}

				if (prevPrice + payload >= 0) {
					applyAdditionalCost(Number(stringifyPrice(payload)));
					setEdit(false);
					return;
				}

				setValue(payload);
				applyAdditionalCost(Number(stringifyPrice(payload)));
				setEdit(false);
			}
			setEdit(false);
		},
		[
			setAction,
			additionalCost,
			watchedPrice,
			setEdit,
			setValue,
			stringifyPrice,
			form,
			syncPriceWithAdditionalCost,
			tab.form?.processedByTaximeter,
		],
	);

	const priseDiscount = useMemo(() => {
		if (discountValue > 0) {
			const multiplier = 10 ** precision;
			const discountPrice =
				Math.round(discountValue * multiplier) / multiplier;
			return `${discountPrice} ${currencyGlobalSettings}`;
		}
		return "";
	}, [discountValue, precision, currencyGlobalSettings]);

	const isPoints = useMemo(
		() =>
			points?.filter((item) => {
				if (!item) return false;
				if (!item.coordinates.lat) return false;
				if (!item.coordinates.lng) return false;
				return true;
			})?.length,
		[points],
	);

	return (
		<StyledRow alignItems="end" gap="0 6px" m="0 0 6px 0">
			<Primary textAlign="start">{`${t([
				`orderPage.order_form.price`,
				"Price",
			])}: `}</Primary>
			<Red
				colors={
					loadingChangeCost || !isPoints
						? Colors.TRANSPARENT
						: Colors.TEXT_RED
				}
			>{`${stringifyPrice(
				loadingChangeCost ? 0 : watchedPrice,
			)} ${currencyGlobalSettings}`}</Red>

			{!disabled && (
				<Clickable onClick={() => setEdit(true)}>
					<EditIcon />
				</Clickable>
			)}

			{edit && (
				<NumberInputMemo
					shadow
					title={t([`orderPage.order_form.price`, "Price"]) ?? ""}
					unit={currencyGlobalSettings ?? ""}
					value={value}
					onSubmit={onChange}
					onClose={() => setEdit(false)}
					useMinSet
					replaceFirst
				/>
			)}

			<Green>{priseDiscount}</Green>
		</StyledRow>
	);
};

export default Price;
